import { format } from 'url';
import { useEventCallback } from '@flex-packages/hooks-common';
import { useRouter } from 'next/router';
import { divideQueryParams, extractPathParamStringsFromPathname } from '../../utils/params.mjs';
import { stripBasePath } from '../../utils/basePath.mjs';

const useNextFlexRouter = () => {
  const {
    asPath: asPathWithoutBasePath,
    pathname: rawPathnameWithoutBasePath,
    query: rawQuery,
    push: rawPush,
    replace: rawReplace,
    back,
    basePath,
    events
  } = useRouter();
  const asPath = [basePath, asPathWithoutBasePath].join("");
  const rawPathname = [basePath, rawPathnameWithoutBasePath].join("");
  const params = extractPathParamStringsFromPathname(rawPathname);
  const { pathParams, queryParams } = divideQueryParams(rawQuery, params);
  const pathname = asPath.split("?")[0];
  const push = useEventCallback(async (url, as, options) => {
    const [sanitizedUrl, canUseNextRouter] = stripBasePath(url, basePath);
    if (!canUseNextRouter) {
      window.location.href = format(sanitizedUrl);
      return;
    }
    const asWithoutBasePath = as !== void 0 ? stripBasePath(as, basePath)[0] : void 0;
    rawPush(sanitizedUrl, asWithoutBasePath, options);
  });
  const replace = useEventCallback(async (url, as, options) => {
    const [sanitizedUrl, canUseNextRouter] = stripBasePath(url, basePath);
    if (!canUseNextRouter) {
      window.location.href = format(sanitizedUrl);
      return;
    }
    const asWithoutBasePath = as !== void 0 ? stripBasePath(as, basePath)[0] : void 0;
    rawReplace(sanitizedUrl, asWithoutBasePath, options);
  });
  const updateQueryParams = useEventCallback(
    async (queryValue, options, as) => {
      const routeFn = options.mode === "replace" ? replace : push;
      return routeFn(
        {
          pathname: rawPathname,
          query: typeof queryValue === "function" ? {
            ...pathParams,
            ...queryValue(queryParams)
          } : {
            // TODO: Setter를 제공하는 상황에서, value로 넘어왔을 때 이전 값(queryParams)을 꼭 spread 해줘야할까?
            // react의 setState - value만 넘어온 경우 spread해주지 않음
            // next-router의 push,replace - value만 넘어온 경우 spread해주지 않음
            ...pathParams,
            ...queryParams,
            ...queryValue
          }
        },
        as,
        options
      );
    }
  );
  const removeQueryParams = useEventCallback(
    (key, option, as) => {
      return updateQueryParams(
        (prev) => {
          const copiedPrevQuery = { ...prev };
          const keyList = typeof key === "string" ? [key] : key;
          keyList.forEach((it) => {
            delete copiedPrevQuery[it];
          });
          return copiedPrevQuery;
        },
        option,
        as
      );
    }
  );
  return {
    asPath,
    back,
    basePath,
    events,
    hash: "",
    pathname,
    pathParams,
    push,
    queryParams,
    rawQuery,
    removeQueryParams,
    replace,
    state: void 0,
    updateQueryParams
  };
};

export { useNextFlexRouter };
//# sourceMappingURL=useNextFlexRouter.mjs.map
